<script setup lang="ts">
import { useUserSubscription } from "~/composables/use-user-subscription";

export interface UserMenuProps {
  fullName?: string;
  withThemeSwitcher?: boolean;
  logoInitialsSize?: number;
}

const { t, locale: currentLocale } = useI18n();
const router = useRouter();
const emit = defineEmits(["logout"]);
const props = defineProps<UserMenuProps>();
const appConfig = useRuntimeConfig().public;
const isMobile = useIsMobile();

// Get subscription status from store (cached via useAsyncData)
const hasSubscription = useUserSubscription();

const hasClips = appConfig.APP_CUSTOMER.web_sdk.enable_clipping;
const availableLocales = appConfig.APP_CUSTOMER.available_languages_list as TAvailableLocale[];
const isVisibleLanguageSelector = computed(() => availableLocales.length > 1);

const isMenuOpen = ref(false);
const activeSection = ref<"language" | "">("");

function openMenu() {
  activeSection.value = "";
  isMenuOpen.value = true;
  if (isMobile.value) {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  }
}

function closeMenu() {
  isMenuOpen.value = false;
  activeSection.value = "";
  if (isMobile.value) {
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }
}

function handleLogout() {
  closeMenu();
  emit("logout");
}

function handleLinkClick(path: string, hideFn?: () => void) {
  if (hideFn) {
    hideFn();
  }
  else {
    closeMenu();
  }
  router.push(path);
}

function handleFollowingNavigate(path: string, hideFn?: () => void) {
  handleLinkClick(path, hideFn);
}

function openMenuSection(section?: "language") {
  activeSection.value = section || "";
}

watch(() => isMobile.value, () => {
  if (!isMobile.value) {
    closeMenu();
  }
});
</script>

<template>
  <div class="flex items-center justify-center">
    <!-- Desktop: Dropdown Menu -->
    <UMenu
      v-if="!isMobile"
      :dropdown="{
        placement: 'bottom-end',
        distance: 14,
      }"
    >
      <template #trigger="{ toggle }">
        <BaseButton name="user-menu" type="icon" title="Menu" @click="() => { activeSection = ''; toggle(); }">
          <IconLogoInitials
            v-if="props.fullName"
            :title="props.fullName"
            :width="props.logoInitialsSize"
            :height="props.logoInitialsSize"
            :has-subscription="hasSubscription"
          />
        </BaseButton>
      </template>
      <template #default="{ hide }">
        <div
          v-auto-animate
          class="bg-white dark:bg-neutral-dark-700 w-[25rem] rounded-lg shadow-lg overflow-hidden"
        >
          <template v-if="activeSection === 'language'">
            <UserMenuSelectionLanguageNew
              :available-locales="availableLocales"
              @back="openMenuSection()"
            />
          </template>
          <template v-else>
            <!-- User Info Section -->
            <div v-if="props.fullName" class="px-6 py-6">
              <div class="flex items-center gap-3">
                <IconLogoInitials
                  :title="props.fullName"
                  :width="40"
                  :height="40"
                  :has-subscription="hasSubscription"
                />
                <div class="flex-1 min-w-0">
                  <p class="font-bold text-[var(--base-text-color)] truncate">
                    {{ props.fullName }}
                  </p>
                </div>
              </div>
            </div>
            <!-- Following Section -->
            <div class="px-6">
              <UserMenuFollowing @navigate="(path) => handleFollowingNavigate(path, hide)" />
            </div>
            <!-- Menu Items -->
            <UList class="!py-4">
              <UListItem
                v-if="isVisibleLanguageSelector"
                :on-click-close-menu="false"
                size="md"
                class="!p-0"
              >
                <BaseButton
                  class="text-link w-full flex items-center gap-3" type="icon"
                  name="header-language-link" @click.capture="openMenuSection('language')"
                >
                  <div class="w-full flex items-center justify-between gap-3 px-6 py-2">
                    <p class="truncate">
                      {{ t('labels.language') }}
                    </p>
                    <div class="truncate text-secondary font-medium flex items-center gap-2">
                      {{ t(`locales.${currentLocale}`) }}
                      <IconChevronRight class="text-[#1A1C1E]" :width="8" :height="8" />
                    </div>
                  </div>
                </BaseButton>
              </UListItem>
              <UListItem size="md" class="!p-0">
                <BaseButton
                  class="text-link w-full flex items-center gap-3" type="icon"
                  name="header-account-link" @click.capture="() => handleLinkClick('/account/general/', hide)"
                >
                  <div class="w-full flex items-center gap-3 px-6 py-2">
                    <span class=" truncate">My Account</span>
                  </div>
                </BaseButton>
              </UListItem>

              <UListItem size="md" class="!p-0">
                <BaseButton
                  class="text-link w-full flex items-center gap-3" type="icon"
                  name="header-videos-link" @click.capture="() => handleLinkClick('/selected/videos/', hide)"
                >
                  <div class="w-full flex items-center gap-3 px-6 py-2">
                    <span class=" truncate">Saved Videos</span>
                  </div>
                </BaseButton>
              </UListItem>
              <UListItem v-if="hasClips" size="md" class="!p-0">
                <BaseButton
                  class="text-link w-full flex items-center gap-3" type="icon"
                  name="header-clips-link" @click.capture="() => handleLinkClick('/selected/highlights/', hide)"
                >
                  <div class="w-full flex items-center gap-3 px-6 py-2">
                    <span class=" truncate">My Highlights</span>
                  </div>
                </BaseButton>
              </UListItem>
              <UListItem size="md" class="!p-0">
                <BaseButton
                  class="text-secondary w-full text-link flex items-center gap-3" type="icon"
                  name="header-logout-button" @click.prevent="handleLogout"
                >
                  <div class="w-full flex items-center gap-3 px-6 py-2">
                    <span class="text-error">{{ t('labels.logout') }}</span>
                  </div>
                </BaseButton>
              </UListItem>
              <template v-if="withThemeSwitcher">
                <UDivider lighter />
                <div class="flex items-center justify-center w-full pt-4 pb-2">
                  <BaseThemeSwitcher has-labels />
                </div>
              </template>
            </UList>
          </template>
        </div>
      </template>
    </UMenu>

    <!-- Mobile: Fullscreen Modal -->
    <template v-else>
      <BaseButton name="user-menu" type="icon" title="Menu" @click="openMenu">
        <IconLogoInitials
          v-if="props.fullName"
          :title="props.fullName"
          :width="props.logoInitialsSize"
          :height="props.logoInitialsSize"
          :has-subscription="hasSubscription"
        />
      </BaseButton>
      <UModal
        v-model="isMenuOpen"
        :fullscreen="true"
        dialog-class="bg-white dark:bg-neutral-dark-800 flex flex-col"
        @close="closeMenu"
      >
        <div class="flex flex-col h-full">
          <!-- Header with User Info and Close Button -->
          <div v-if="props.fullName" class="flex items-center justify-between px-6 py-4">
            <div class="flex items-center gap-4 flex-1 min-w-0">
              <IconLogoInitials
                :title="props.fullName"
                :width="56"
                :height="56"
                :has-subscription="hasSubscription"
              />
              <div class="flex-1 min-w-0">
                <p class="text-base font-bold text-[var(--base-text-color)] truncate">
                  {{ props.fullName }}
                </p>
              </div>
            </div>
            <BaseButton
              title="close"
              name="close-menu"
              type="icon"
              class="ml-4 flex-shrink-0"
              @click="closeMenu"
            >
              <IconClose class="text-[var(--base-icon-color)]" />
            </BaseButton>
          </div>
          <!-- Menu Items -->
          <div v-auto-animate class="flex-1 overflow-y-auto">
            <template v-if="activeSection === 'language'">
              <UserMenuSelectionLanguageNew
                :available-locales="availableLocales"
                @back="openMenuSection()"
              />
            </template>
            <div v-else class="flex flex-col">
              <!-- Following Section -->
              <div class="px-6 py-4">
                <UserMenuFollowing @navigate="handleFollowingNavigate" />
              </div>
              <ul>
                <li v-if="isVisibleLanguageSelector" class="px-6 py-4">
                  <BaseButton
                    class="text-link w-full flex items-center gap-4"
                    type="icon"
                    name="header-language-link"
                    @click.capture="openMenuSection('language')"
                  >
                    <div class="w-full flex items-center justify-between gap-4">
                      <p class="text-base truncate">
                        {{ t('labels.language') }}
                      </p>
                      <div class="truncate text-secondary font-medium flex items-center gap-2">
                        {{ t(`locales.${currentLocale}`) }}
                        <IconChevronRight class="text-[#1A1C1E]" :width="8" :height="8" />
                      </div>
                    </div>
                  </BaseButton>
                </li>
                <li class="px-6 py-4">
                  <BaseButton
                    class="text-link w-full flex items-center gap-4" type="icon"
                    name="header-account-link" @click.capture="() => handleLinkClick('/account/general/')"
                  >
                    <span class="text-base ">{{ t('labels.account') }}</span>
                  </BaseButton>
                </li>
                <li class="px-6 py-4">
                  <BaseButton
                    class="text-link w-full flex items-center gap-4" type="icon"
                    name="header-videos-link" @click.capture="() => handleLinkClick('/selected/videos/')"
                  >
                    <span class="text-base ">{{ t('labels.selected_videos') }}</span>
                  </BaseButton>
                </li>
                <li v-if="hasClips" class="px-6 py-4">
                  <BaseButton
                    class="text-link w-full flex items-center gap-4"
                    type="icon"
                    name="header-clips-link"
                    @click.capture="() => handleLinkClick('/selected/highlights/')"
                  >
                    <span class="text-base ">{{
                      t('labels.my_clips_and_highlights')
                    }}</span>
                  </BaseButton>
                </li>
                <li class="px-6 py-4">
                  <BaseButton
                    class="text-secondary w-full text-link flex items-center gap-4" type="icon"
                    name="header-logout-button" @click.prevent="handleLogout"
                  >
                    <span class="text-base text-error">{{ t('labels.logout') }}</span>
                  </BaseButton>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </UModal>
    </template>
  </div>
</template>
